import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import {
  emptyParameterInfo,
  extractActiveParamKeys,
  IParameterInfo,
  IParameterOption,
} from '~/src/dash/components/forms/formBuilder/common';
import { FormBuilderContext } from '~/src/dash/components/forms/formBuilder/contexts';
import { ParameterRow } from '~/src/dash/components/forms/formBuilder/ParameterRow';
import { Row, Rows, spacing, TextButton } from '~/src/dash/publicaUI';

const getInitialParameters = (
  initialParameterKeys: string[],
  parameterOptions: IParameterOption[],
  cfg: Record<string, IParameterInfo>,
): IParameterInfo[] => {
  if (isEmpty(initialParameterKeys)) return [{ ...emptyParameterInfo, reactKey: 0 }];
  const parameters: IParameterInfo[] = [];
  const possibleKeys = parameterOptions.map((option) => {
    return option.key;
  });
  initialParameterKeys.forEach((key) => {
    if (!isEmpty(cfg[key]) && possibleKeys.includes(key)) {
      parameters.push(cfg[key]);
    }
  });
  return parameters;
};

interface IProps {
  defaultObject: Record<string, any>;
  lists: Record<string, ISelectOption[]>;
  object: Record<string, any>;
  parameterCfg: Record<string, IParameterInfo>;
  parameterOptions: IParameterOption[];
  initialParameterKeys: string[];
  isLoading: boolean;
}

export const FormBuilder = ({
  isLoading,
  defaultObject,
  lists,
  object,
  parameterCfg,
  parameterOptions,
  initialParameterKeys,
}: IProps) => {
  const [parameters, setParameters] = useState<IParameterInfo[]>(
    getInitialParameters(initialParameterKeys, parameterOptions, parameterCfg),
  );
  const [counter, setCounter] = useState(1);
  const isMounted = useRef(false);

  useEffect(() => {
    if (isMounted.current) {
      setParameters(getInitialParameters(initialParameterKeys, parameterOptions, parameterCfg));
    } else {
      isMounted.current = true;
    }
  }, [initialParameterKeys, parameterOptions]);

  const handleAddParameter = () => {
    unstable_batchedUpdates(() => {
      setParameters((prevParams) => {
        const clonedParameters = cloneDeep(prevParams);
        clonedParameters.push({
          ...emptyParameterInfo,
          reactKey: counter,
        });
        return clonedParameters;
      });
      setCounter(counter + 1);
    });
  };

  const handleRemoveParameter = (idx: number) => {
    setParameters((prevParams) => {
      const clonedParameters = cloneDeep(prevParams);
      clonedParameters.splice(idx, 1);
      return clonedParameters;
    });
  };

  const handleSelectParameter = (idx: number, key: string) => {
    setParameters((prevParams) => {
      const clonedParameters = cloneDeep(prevParams);
      const reactKey = clonedParameters[idx].reactKey;
      clonedParameters[idx] = parameterCfg[key];
      clonedParameters[idx].reactKey = reactKey;
      return clonedParameters;
    });
  };

  const activeParamKeys = extractActiveParamKeys(parameters);
  object.ActiveParameters = Array.from(activeParamKeys).join(',');

  if (isLoading) return null;

  return (
    <FormBuilderContext.Provider
      value={{
        activeParamKeys,
        cfg: parameterCfg,
        defaultObject,
        handleRemoveParameter,
        handleSelectParameter,
        lists,
        object,
        options: parameterOptions,
      }}
    >
      <Rows gap={spacing.small} style={{ minWidth: '800px', maxWidth: '960px' }}>
        {parameters.map((param, index) => {
          const isRemoveDisabled = parameters.length === 1 && index === 0;
          // if new/blank parameter, assign it a unique key
          const updatedKey = param.label || param.key + String(param.reactKey);

          return (
            <Row noPadding key={updatedKey}>
              <ParameterRow
                param={param}
                index={index}
                isRemoveDisabled={isRemoveDisabled}
                key={updatedKey}
              />
            </Row>
          );
        })}
        <Row noHorizontalPadding smallVerticalPadding>
          <TextButton size='small' onClick={handleAddParameter}>
            + Add Parameter
          </TextButton>
        </Row>
      </Rows>
    </FormBuilderContext.Provider>
  );
};
